/**
 * Bridge between the axios layer and the globally mounted
 * `TransitionNoteHost` dialog.
 *
 * When the server blocks a stage/status move with `data.noteRequired` (a
 * "Notes" requirement on the target status), the axios interceptor calls
 * `requestTransitionNote` to ask the user for a note, then retries the move
 * with `note` added to the payload. The host registers itself here on mount.
 *
 * Kept in `lib` (dependency-free) so `lib/axios` never imports feature code.
 */

type NotePromptHandler = (message: string) => Promise<string | null>;
/** Extra context for the follow-up prompt, straight from the 400's data. */
export interface FollowUpPromptOptions {
  /** "Maximum days allowed" for the entity's current bucket; null = no cap. */
  maxDays?: number | null;
}
/** Resolves with the chosen follow-up date (ISO), or null on cancel. */
type FollowUpPromptHandler = (
  message: string,
  opts?: FollowUpPromptOptions,
) => Promise<string | null>;

let handler: NotePromptHandler | null = null;
let followUpHandler: FollowUpPromptHandler | null = null;

/** Serialises concurrent prompts so two failures never race one dialog. */
let queue: Promise<unknown> = Promise.resolve();

export function registerTransitionNotePrompt(h: NotePromptHandler | null): void {
  handler = h;
}

/**
 * Ask the user for a note. Resolves with the note text, or null when the user
 * cancels or no host is mounted (e.g. auth pages) — callers then surface the
 * original server error as usual.
 */
export function requestTransitionNote(message: string): Promise<string | null> {
  if (!handler) return Promise.resolve(null);
  const run = queue.then(() => (handler ? handler(message) : null));
  queue = run.catch(() => null);
  return run;
}

export function registerTransitionFollowUpPrompt(
  h: FollowUpPromptHandler | null,
): void {
  followUpHandler = h;
}

/**
 * Ask the user to schedule a follow-up (a "FollowUps" requirement on the
 * target status). Resolves with an ISO date string, or null when the user
 * cancels or no host is mounted.
 *
 * Shares the note prompt's `queue`, so a status gated on BOTH a note and a
 * follow-up asks one question at a time instead of stacking two dialogs.
 */
export function requestTransitionFollowUp(
  message: string,
  opts?: FollowUpPromptOptions,
): Promise<string | null> {
  if (!followUpHandler) return Promise.resolve(null);
  const run = queue.then(() =>
    followUpHandler ? followUpHandler(message, opts) : null,
  );
  queue = run.catch(() => null);
  return run;
}
